import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Clock,
  FileText,
  User,
  Calendar,
  CreditCard,
  DollarSign,
  Percent,
  Eye,
  Phone,
  Download,
  Printer,
  Share2,
  Trash,
} from "lucide-react";
import {
  FaMoneyBillWave,
  FaCreditCard,
  FaUniversity,
  FaShieldAlt,
  FaGlobe,
  FaCalendarCheck,
  FaReceipt,
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaUndo,
  FaTimesCircle,
  FaPause,
  FaBarcode,
  FaQrcode,
} from "react-icons/fa";

import qrGenerator from "../../../../../utils/qrGenerator.js";
import formatDate from "../../../../../utils/formatDate.js";
import Card, {
  CardSkeleton,
  StatCardSkeleton,
} from "../../../../common/Card.jsx";
import StatCard from "../../../../common/StatCard.jsx";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiFetchError, apiRequest, apiSuccess } from "../../../../../redux/api/apiActions.js";
import { clearConfirmationAlerts, hideAlert, showAlert } from "../../../../../redux/alert/alertActions.js";
import { useDispatch } from "react-redux";
import api from "../../../../../api.js";
import { downloadInvoice, generateInvoice } from "../../../../../utils/useInvoiceGenerator.js";
import { useUser } from "../../../../../authentication/authentication.js";

// Import the Text component utilities
import Txt from "../../../../Text.jsx";
import { arClass } from "../../../../../utils/useArClass";
import textTranslator from "../../../../../utils/useTextTranslator";

const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const useTheme = () => {
  const darkMode = useSelector((state) => state.settings.darkMode);

  return {
    darkMode,
    colors: {
      bg: {
        primary: darkMode ? "bg-gray-950" : "bg-gray-50",
        secondary: darkMode ? "bg-gray-900" : "bg-white",
        card: darkMode ? "bg-gray-900/50" : "bg-white",
        hover: darkMode ? "hover:bg-gray-800/80" : "hover:bg-gray-50",
      },
      text: {
        primary: darkMode ? "text-gray-100" : "text-gray-900",
        secondary: darkMode ? "text-gray-400" : "text-gray-600",
        muted: darkMode ? "text-gray-500" : "text-gray-500",
      },
      border: {
        primary: darkMode ? "border-gray-800" : "border-gray-200",
        secondary: darkMode ? "border-gray-700" : "border-gray-300",
      },
      status: {
        paid: darkMode
          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
          : "bg-emerald-100 text-emerald-800 border-emerald-200",
        pending: darkMode
          ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
          : "bg-amber-100 text-amber-800 border-amber-200",
        failed: darkMode
          ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
          : "bg-rose-100 text-rose-800 border-rose-200",
        refunded: darkMode
          ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
          : "bg-indigo-100 text-indigo-800 border-indigo-200",
        "partially-paid": darkMode
          ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
          : "bg-yellow-100 text-yellow-800 border-yellow-200",
        cancelled: darkMode
          ? "bg-red-500/10 text-red-400 border-red-500/20"
          : "bg-red-100 text-red-800 border-red-200",
        "on-hold": darkMode
          ? "bg-gray-500/10 text-gray-400 border-gray-500/20"
          : "bg-gray-100 text-gray-800 border-gray-200",
      },
      accent: {
        primary: darkMode
          ? "bg-gradient-to-r from-green-600 to-emerald-600"
          : "bg-gradient-to-r from-green-500 to-emerald-500",
        hover: darkMode
          ? "hover:from-green-700 hover:to-emerald-700"
          : "hover:from-green-600 hover:to-emerald-600",
        text: "text-white",
      },
    },
  };
};
/* ---------------- Invoice Component ---------------- */
const InvoiceView = ({ payment }) => {
  const theme = useTheme();
  const localUser = useUser();
  const dispatch = useDispatch();
  const userKey = `user_${localUser.id}`;
  const deleteInvoiceKey = `delete_invoice_${payment.id}`;
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const [qrSrc, setQrSrc] = useState("");
  const [user, setUser] = useState(null);
  const [clinicLogo, setClinicLogo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [sharing, setSharing] = useState(false);

  const invoiceRef = useRef(null);

  const safeDisplay = (val, fallback = "—") => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === "number")
      return Number.isFinite(val) ? String(val) : fallback;
    return String(val);
  };

  const invoiceData = {
    invoiceNumber: safeDisplay(payment.invoice?.invoiceNumber, "N/A"),
    invoiceDate: formatDate(payment.invoice?.invoiceDate, false) || "N/A",
    barCode: payment.invoice?.barCode || "N/A",
    paymentId: safeDisplay(payment?.id, "N/A"),
    patientName:
      `${payment?.patient?.firstName || ""} ${
        payment?.patient?.lastName || ""
      }`.trim() || "N/A",
    amount: payment?.amount ? `$${Number(payment.amount).toFixed(2)}` : "N/A",
    finalAmount: payment?.amountAfterDiscount
      ? `$${Number(payment.amountAfterDiscount).toFixed(2)}`
      : "N/A",
    paymentMethod: payment?.method || "N/A",
    paymentStatus: payment?.paymentStatus || "N/A",
  };

  useEffect(() => {
    dispatch(apiRequest("GET", userKey));
    api
      .get(`users/${localUser.id}`, { delay: 0 })
      .then((res) => {
        const userData = res.data.data;
        setUser(userData);

        if (userData.clinic.logo) {
          setClinicLogo(userData.clinic.logo);
        }
        dispatch(apiSuccess("GET", userKey, "User fetched successfully"));
      })
      .catch((error) => {
        // Handle error silently
        const errorMessage = error.response?.data?.error;
        const type = error.response?.data?.type;
        errorChecker(errorMessage, type, dispatch, errorMessage);
      });
  }, [localUser?.id, dispatch]);

  useEffect(() => {
    if (!payment.invoice) return;

    const generate = async () => {
      const data = `
        ORGANIZATION: ${user?.clinic?.name || "N/A"}
        Invoice Number: ${invoiceData.invoiceNumber}
        Invoice Date: ${invoiceData.invoiceDate}
        Patient: ${invoiceData.patientName}
        Amount: ${invoiceData.amount}
        Final Amount: ${invoiceData.finalAmount}
        Payment Method: ${invoiceData.paymentMethod}
        Status: ${invoiceData.paymentStatus}
        Verification Badge: ${invoiceData.barCode}
        Url: ${window.location.origin}/payments/${payment.invoice?.id}
      `.trim();

      const qr = await qrGenerator(data);
      setQrSrc(qr);
    };

    generate();
  }, [payment.invoice, payment]);

  const getInvoiceStatusLabel = (invoice, payment) => {
    const darkMode = theme.darkMode;
    if (!invoice) {
      return {
        label: <Text>No Invoice</Text>,
        style: darkMode
          ? "status !bg-gray-700 !text-gray-300 shadow-lg border border-gray-600 !w-max text-xs"
          : "status !bg-gray-300 !text-gray-900 shadow-lg border border-gray-900/40 !w-max text-xs",
        icon: <FaFileAlt className="inline mr-1" size={14} />,
      };
    }

    const paymentStatus = (payment?.paymentStatus || "").toLowerCase();

    switch (paymentStatus) {
      case "paid":
      case "completed":
        return {
          label: textTranslator("paid", "PAYMENTS"),
          style: darkMode
            ? "status !bg-green-900/30 !text-green-300 shadow-lg border border-green-700 !w-max text-xs"
            : "status !bg-green-50 !text-green-700 shadow-lg border border-green-200 !w-max text-xs",
          icon: <FaCheckCircle className="inline mr-1" size={14} />,
        };

      case "pending":
        return {
          label: textTranslator("pending", "PAYMENTS"),
          style: darkMode
            ? "status !bg-orange-900/30 !text-orange-300 shadow-lg border border-orange-700 !w-max text-xs"
            : "status !bg-orange-50 !text-orange-700 shadow-lg border border-orange-200 !w-max text-xs",
          icon: <FaClock className="inline mr-1" size={14} />,
        };

      case "failed":
        return {
          label: textTranslator("failed", "PAYMENTS"),
          style: darkMode
            ? "status !bg-red-900/30 !text-red-300 shadow-lg border border-red-700 !w-max text-xs"
            : "status !bg-red-50 !text-red-700 shadow-lg border border-red-200 !w-max text-xs",
          icon: <FaExclamationTriangle className="inline mr-1" size={14} />,
        };

      case "refunded":
        return {
          label: textTranslator("refunded", "PAYMENTS"),
          style: darkMode
            ? "status !bg-indigo-900/30 !text-indigo-300 shadow-lg border border-indigo-700 !w-max text-xs"
            : "status !bg-indigo-50 !text-indigo-700 shadow-lg border border-indigo-200 !w-max text-xs",
          icon: <FaUndo className="inline mr-1" size={14} />,
        };

      default:
        return {
          label: <Text>Generated</Text>,
          style: darkMode
            ? "status !bg-blue-900/30 !text-blue-300 shadow-lg border border-blue-700 !w-max text-xs"
            : "status !bg-blue-50 !text-blue-700 shadow-lg border border-blue-200 !w-max text-xs",
          icon: <FaFileAlt className="inline mr-1" size={14} />,
        };
    }
  };

  const getMethod = (method) => {
    const darkMode = theme.darkMode;
    const methodConfig = {
      cash: {
        label: <Text>cash</Text>,
        value: "cash",
        icon: <FaMoneyBillWave className="inline mr-1" size={14} />,
      },
      "credit-card": {
        label: <Text>credit card</Text>,
        value: "credit-card",
        icon: <FaCreditCard className="inline mr-1" size={14} />,
      },
      "debit-card": {
        label: <Text>debit card</Text>,
        value: "debit-card",
        icon: <FaUniversity className="inline mr-1" size={14} />,
      },
      insurance: {
        label: <Text>insurance</Text>,
        value: "insurance",
        icon: <FaShieldAlt className="inline mr-1" size={14} />,
      },
      "online-payment": {
        label: <Text>online payment</Text>,
        value: "online-payment",
        icon: <FaGlobe className="inline mr-1" size={14} />,
      },
      other: {
        label: <Text>other</Text>,
        value: "other",
        icon: <FaFileAlt className="inline mr-1" size={14} />,
      },
    };

    const lowerMethod = method?.toLowerCase();
    const config = methodConfig[lowerMethod] || methodConfig.other;

    const darkModeStyles = {
      cash: "bg-green-900/30 text-green-300 border border-green-700",
      "credit-card": "bg-blue-900/30 text-blue-300 border border-blue-700",
      "debit-card": "bg-purple-900/30 text-purple-300 border border-purple-700",
      insurance: "bg-orange-900/30 text-orange-300 border border-orange-700",
      "online-payment": "bg-cyan-900/30 text-cyan-300 border border-cyan-700",
      other: "bg-gray-700 text-gray-300 border border-gray-600",
    };

    const lightModeStyles = {
      cash: "bg-green-50 text-green-700 border border-green-200",
      "credit-card": "bg-blue-50 text-blue-700 border border-blue-200",
      "debit-card": "bg-purple-50 text-purple-700 border border-purple-200",
      insurance: "bg-orange-50 text-orange-700 border border-orange-200",
      "online-payment": "bg-cyan-50 text-cyan-700 border border-cyan-200",
      other: "bg-gray-50 text-gray-700 border border-gray-200",
    };

    const style = darkMode
      ? darkModeStyles[lowerMethod] || darkModeStyles.other
      : lightModeStyles[lowerMethod] || lightModeStyles.other;

    return {
      ...config,
      style: `flex items-center gap-1 p-2 rounded-full shadow-lg font-medium text-sm !w-max text-xs max-sm:text-xss max-sm:p-1.5 ${style}`,
    };
  };

  const handlePrint = async () => {
    try {
      setPrinting(true);
      const pdfInvoice = await generateInvoice("pdf", {
        ...payment,
        qrCodeImage: qrSrc,
        user: user,
        clinicLogo: clinicLogo,
      });

      const url = pdfInvoice.url || URL.createObjectURL(pdfInvoice.blob);
      window.open(url, "_blank");
    } catch (err) {
      // Handle error silently
      dispatch(
        showAlert({
          type: "error",
          title: textTranslator("Print Failed", "PAYMENTS"),
          message: textTranslator("unableToDownloadInvoice", "PAYMENTS"),
        })
      );
    } finally {
      setPrinting(false);
    }
  };

  const handleShare = async () => {
    try {
      setSharing(true);
      const pdfInvoice = await generateInvoice("pdf", {
        ...payment,
        qrCodeImage: qrSrc,
        user: user,
        clinicLogo: clinicLogo,
      });

      const file = new File([pdfInvoice.blob], pdfInvoice.fileName, {
        type: pdfInvoice.type,
      });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Medical Invoice",
          text: "Here is your invoice.",
          files: [file],
        });
      } else {
        // Handle error silently
        downloadInvoice(pdfInvoice);
      }
    } catch (err) {
      // Handle error silently
      dispatch(
        showAlert({
          type: "error",
          title: textTranslator("shareFailed", "PAYMENTS"),
          message: textTranslator("unableToShareInvoice", "PAYMENTS"),
        })
      );
    } finally {
      setSharing(false);
    }
  };

  const download = async () => {
    if (!qrSrc || !user) return;
    try {
      setDownloading(true);
      const result = await generateInvoice("pdf", {
        ...payment,
        qrCodeImage: qrSrc,
        user: user,
        clinicLogo: clinicLogo,
      });
      downloadInvoice(result, payment.invoice.invoiceNumber);
    } catch (err) {
      // Handle error silently
      dispatch(
        showAlert({
          type: "error",
          title: textTranslator("downloadFailed", "PAYMENTS"),
          message: textTranslator("unableToDownloadInvoice", "PAYMENTS"),
        })
      );
    } finally {
      setDownloading(false);
    }
  };

  const cancelDelete = (id) => {
    dispatch(hideAlert(id));
  };

  const handleDelete = () => {
    const invoice = payment.invoice;
    if (!invoice) return;
    dispatch(clearConfirmationAlerts());
    dispatch(
      showAlert({
        id: `delete-confirmation-${invoice.id}`,
        type: "confirmation",
        title: textTranslator("deleteInvoice", "PAYMENTS"),
        message: textTranslator("deleteInvoiceConfirm", "PAYMENTS"),
        onCancel: cancelDelete,
        onConfirm: () => confirmDelete(invoice),
      })
    );
  };

  const confirmDelete = (invoice) => {
    dispatch(apiRequest("DELETE", deleteInvoiceKey));
    api
      .delete(`payments/invoice-id/${invoice.id}`)
      .then((res) => {
        dispatch(
          apiSuccess("DELETE", deleteInvoiceKey, textTranslator("invoiceDeleted", "PAYMENTS"))
        );

        dispatch(
          showAlert({
            type: "success",
            title: textTranslator("Deleted", "PAYMENTS"),
            message: textTranslator("invoiceDeleted", "PAYMENTS"),
          })
        );
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.error;
        dispatch(apiFetchError(errorMessage, "DELETE", deleteInvoiceKey));
        dispatch(
          showAlert({
            type: "error",
            title: textTranslator("deleteFailed", "PAYMENTS"),
            message: errorMessage || textTranslator("deleteFailed", "PAYMENTS"),
          })
        );
      });
  };

  return (
    <div className={arClass("space-y-6 max-sm:space-y-4")}>
      {/* Modern Header with Glassmorphism */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={arClass(`relative overflow-hidden rounded-2xl p-8 max-sm:p-4 max-sm:rounded-xl ${
          theme.darkMode
            ? "bg-slate-800 border border-slate-700"
            : "bg-white border border-slate-200"
        }`)}
      >
        <div className={arClass("relative z-10 flex items-start justify-between max-sm:flex-col max-sm:gap-4")}>
          <div className={arClass("flex items-center gap-4 max-sm:gap-3")}>
            <motion.div
              animate={{
                opacity: [1, 0.8, 1],
                y: [0, -4, 0],
                rotate: [0, 2, -2, 0],
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className={arClass(`p-4 rounded-2xl max-sm:p-3 max-sm:rounded-xl ${
                theme.darkMode ? "bg-blue-600" : "bg-blue-500"
              }`)}
            >
              <FaFileAlt className={arClass("w-8 h-8 text-white max-sm:w-6 max-sm:h-6")} />
            </motion.div>
            <div className={arClass("max-sm:flex-1 max-sm:min-w-0")}>
              <h2
                className={arClass(`text-3xl font-bold max-sm:text-lg max-sm:truncate ${
                  theme.darkMode ? "text-white" : "text-slate-900"
                }`)}
              >
                <Text>Invoice Details</Text>
              </h2>
              <p
                className={arClass(`text-sm mt-1 max-sm:text-xs max-sm:mt-0.5 ${
                  theme.darkMode ? "text-slate-400" : "text-slate-600"
                }`)}
              >
                <Text>Complete invoice information and billing summary</Text>
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={arClass("flex items-center gap-2 max-sm:mt-4 max-sm:w-full max-sm:justify-between max-sm:gap-1")}>
            {/* Print Button */}
            <div className={arClass("relative group max-sm:flex max-sm:flex-col max-sm:items-center")}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrint}
                disabled={printing || downloading || sharing}
                className={arClass(`p-3 rounded-xl border transition-colors flex items-center justify-center group max-sm:p-2 ${
                  (printing || downloading || sharing) ? "opacity-60 cursor-not-allowed" : ""
                } ${theme.darkMode
                  ? "bg-orange-700 border-orange-600 hover:bg-orange-600 text-white"
                  : "bg-white border-orange-300 hover:bg-orange-50 text-orange-700"
                }`)}
              >
                {printing ? (
                  <span className={arClass("flex items-center gap-2 max-sm:flex-col")}>
                    <svg className={arClass("w-4 h-4 animate-spin max-sm:w-3 max-sm:h-3")} viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"></path>
                    </svg>
                    <span className={arClass("text-sm max-sm:text-xs")}>
                      <Text>Printing...</Text>
                    </span>
                  </span>
                ) : (
                  <Printer className={arClass("w-5 h-5 max-sm:w-4 max-sm:h-4")} />
                )}
                <span
                  className={arClass(`
                    absolute left-1/2 -translate-x-1/2 mt-8 text-xs px-2 py-1 rounded shadow top-5
                    opacity-0 translate-y-1 transition-all duration-200 pointer-events-none 
                    group-hover:opacity-100 group-hover:translate-y-0 z-[1000]
                    ${
                      theme.darkMode
                        ? "bg-orange-600 !text-orange-200 hover:bg-orange-700"
                        : "bg-orange-300 text-orange-700 hover:bg-orange-400"
                    }
                    before:content-[''] before:absolute before:top-[-8px] before:left-1/2 before:-translate-x-1/2
                    before:border-4 before:border-transparent 
                    ${
                      theme.darkMode
                        ? "before:border-b-orange-600"
                        : "before:border-b-orange-300"
                    }
                  `)}
                >
                  <Text>print</Text>
                </span>
              </motion.button>
            </div>

            {/* Download Button */}
            <div className={arClass("relative group max-sm:flex max-sm:flex-col max-sm:items-center")}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={download}
                disabled={downloading}
                className={arClass(`p-3 rounded-xl border transition-colors flex items-center justify-center group max-sm:p-2 ${
                  downloading ? "opacity-60 cursor-not-allowed" : ""
                } ${theme.darkMode
                  ? "bg-green-700 border-green-600 hover:bg-green-600 text-white"
                  : "bg-white border-green-300 hover:bg-green-50 text-green-700"
                }`)}
              >
                {downloading ? (
                  <span className={arClass("flex items-center gap-2 max-sm:flex-col")}>
                    <svg className={arClass("w-4 h-4 animate-spin max-sm:w-3 max-sm:h-3")} viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"></path>
                    </svg>
                    <span className={arClass("text-sm max-sm:text-xs")}>
                      <Text>Generating...</Text>
                    </span>
                  </span>
                ) : (
                  <Download className={arClass("w-5 h-5 max-sm:w-4 max-sm:h-4")} />
                )}
                <span
                  className={arClass(`
                    absolute left-1/2 -translate-x-1/2 mt-8 text-xs px-2 py-1 rounded shadow top-5
                    opacity-0 translate-y-1 transition-all duration-200 pointer-events-none
                    group-hover:opacity-100 group-hover:translate-y-0 z-[1000]
                    ${
                      theme.darkMode 
                        ? "bg-green-600 !text-green-200 hover:bg-green-700"
                        : "bg-green-300 text-green-700 hover:bg-green-400"
                    }
                    before:content-[''] before:absolute before:top-[-8px] before:left-1/2 before:-translate-x-1/2
                    before:border-4 before:border-transparent
                    ${
                      theme.darkMode
                        ? "before:border-b-green-600"
                        : "before:border-b-green-300"
                    }
                  `)}
                >
                  <Text>download</Text>
                </span>
              </motion.button>
            </div>

            {/* Share Button */}
            <div className={arClass("relative group max-sm:flex max-sm:flex-col max-sm:items-center")}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleShare}
                disabled={sharing || downloading || printing}
                className={arClass(`p-3 rounded-xl border transition-colors flex items-center justify-center group max-sm:p-2 ${
                  (sharing || downloading || printing) ? "opacity-60 cursor-not-allowed" : ""
                } ${theme.darkMode
                  ? "bg-blue-700 border-blue-600 hover:bg-blue-600 text-white"
                  : "bg-white border-blue-300 hover:bg-blue-50 text-blue-700"
                }`)}
              >
                {sharing ? (
                  <span className={arClass("flex items-center gap-2 max-sm:flex-col")}>
                    <svg className={arClass("w-4 h-4 animate-spin max-sm:w-3 max-sm:h-3")} viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"></path>
                    </svg>
                    <span className={arClass("text-sm max-sm:text-xs")}>
                      <Text>Sharing...</Text>
                    </span>
                  </span>
                ) : (
                  <Share2 className={arClass("w-5 h-5 max-sm:w-4 max-sm:h-4")} />
                )}
                <span
                  className={arClass(`
                    absolute left-1/2 -translate-x-1/2 mt-8 text-xs px-2 py-1 rounded shadow top-5
                    opacity-0 translate-y-1 transition-all duration-200 pointer-events-none 
                    group-hover:opacity-100 group-hover:translate-y-0 z-[1000]
                    ${
                      theme.darkMode
                        ? "bg-blue-600 !text-blue-200 hover:bg-blue-700"
                        : "bg-blue-300 text-blue-700 hover:bg-blue-400"
                    }
                    before:content-[''] before:absolute before:top-[-8px] before:left-1/2 before:-translate-x-1/2
                    before:border-4 before:border-transparent 
                    ${
                      theme.darkMode
                        ? "before:border-b-blue-600"
                        : "before:border-b-blue-300"
                    }
                  `)}
                >
                  <Text>share</Text>
                </span>
              </motion.button>
            </div>

            {/* Delete Button */}
            <div className={arClass("relative group max-sm:flex max-sm:flex-col max-sm:items-center")}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDelete}
                className={arClass(`p-3 rounded-xl border transition-colors flex items-center justify-center reletive group max-sm:p-2 ${
                  theme.darkMode
                    ? "bg-red-700 border-red-600 hover:bg-red-600 text-white"
                    : "bg-white border-red-300 hover:bg-red-50 text-red-700"
                }`)}
              >
                <Trash className={arClass("w-5 h-5 max-sm:w-4 max-sm:h-4")} />
                <span
                  className={arClass(`
                    absolute left-1/2 -translate-x-1/2 mt-8 text-xs px-2 py-1 rounded shadow top-5
                    opacity-0 translate-y-1 transition-all duration-200 pointer-events-none 
                    group-hover:opacity-100 group-hover:translate-y-0 z-[1000]
                    ${
                      theme.darkMode
                        ? "bg-red-600 !text-red-200 hover:bg-red-700"
                        : "bg-red-300 text-red-700 hover:bg-red-400"
                    }
                    before:content-[''] before:absolute before:top-[-8px] before:left-1/2 before:-translate-x-1/2
                    before:border-4 before:border-transparent 
                    ${
                      theme.darkMode
                        ? "before:border-b-red-600"
                        : "before:border-b-red-300"
                    }
                  `)}
                >
                  <Text>deleteInvoice</Text>
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      <div className={arClass("grid grid-cols-1 lg:grid-cols-2 gap-6 max-sm:gap-4 mt-4")}>
        {/* Invoice Details */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="group"
        >
          <Card
            className={arClass(`${
              theme.darkMode
                ? "bg-slate-800/50 border border-slate-700/50"
                : "bg-white border border-slate-200"
            } transition-all duration-300 overflow-hidden`)}
          >
            <div
              className={arClass(`relative px-6 py-4 border-b max-sm:px-4 max-sm:py-3 ${
                theme.darkMode
                  ? "border-slate-700/50 bg-blue-900/30"
                  : "border-slate-200 bg-blue-50"
              }`)}
            >
              <div className={arClass("flex items-center justify-between max-sm:flex-col max-sm:items-start max-sm:gap-3 ")}>
                <div className={arClass("flex items-center gap-4 max-sm:gap-3")}>
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className={arClass(`p-3 rounded-xl max-sm:p-2 ${
                      theme.darkMode ? "bg-blue-600" : "bg-blue-500"
                    } shadow-sm`)}
                  >
                    <FaFileAlt className={arClass("w-6 h-6 text-white max-sm:w-5 max-sm:h-5")} />
                  </motion.div>
                  <div className={arClass("max-sm:flex-1 max-sm:min-w-0")}>
                    <h3
                      className={arClass(`text-xl font-bold max-sm:text-lg ${
                        theme.darkMode ? "text-white" : "text-slate-900"
                      }`)}
                    >
                      <Text>Invoice Information</Text>
                    </h3>
                    <p
                      className={arClass(`text-sm max-sm:text-xs ${
                        theme.darkMode ? "text-slate-400" : "text-slate-600"
                      }`)}
                    >
                      <Text>Billing details and transaction information</Text>
                    </p>
                  </div>
                </div>

                <span
                  className={arClass(`text-sm flex items-center rounded-full p-2 w-full gap-1 max-sm:text-xs max-sm:p-1.5 max-sm:mt-2 ${
                    getInvoiceStatusLabel(payment.invoice, payment).style
                  }`)}
                >
                  {getInvoiceStatusLabel(payment.invoice, payment).icon}
                  <span className={arClass("max-sm:truncate max-sm:text-xs")}>
                    {getInvoiceStatusLabel(payment.invoice, payment).label}
                  </span>
                </span>
              </div>
            </div>

            <div className={arClass("p-6 max-sm:p-4")}>
              <div className={arClass("space-y-3 max-sm:space-y-2")}>
                {[
                  {
                    label: textTranslator("Invoice", "PAYMENTS"),
                    value: payment.invoice?.invoiceNumber,
                    icon: FileText,
                  },
                  {
                    label: textTranslator("Invoice Date", "PAYMENTS"),
                    value: payment.invoice?.invoiceDate
                      ? formatDate(payment.invoice.invoiceDate, false)
                      : "—",
                    icon: Calendar,
                  },
                  {
                    label: textTranslator("Payment Method", "PAYMENTS"),
                    value: payment?.method,
                    icon: CreditCard,
                  },
                  {
                    label: textTranslator("Original Amount", "PAYMENTS"),
                    value: payment?.amount
                      ? `$${Number(payment.amount).toFixed(2)}`
                      : "$0.00",
                    icon: DollarSign,
                  },
                  {
                    label: textTranslator("Discount Applied", "PAYMENTS"),
                    value: payment?.discountPercent
                      ? `${payment.discountPercent}%`
                      : "0%",
                    icon: Percent,
                  },
                  {
                    label: textTranslator("Final Amount", "PAYMENTS"),
                    value: payment?.amountAfterDiscount
                      ? `$${Number(payment.amountAfterDiscount).toFixed(2)}`
                      : "$0.00",
                    icon: DollarSign,
                  },
                  {
                    label: textTranslator("Created At", "PAYMENTS"),
                    value: payment.invoice?.createdAt
                      ? formatDate(payment.invoice.createdAt, true)
                      : "—",
                    icon: Calendar,
                  },
                  {
                    label: textTranslator("Updated At", "PAYMENTS"),
                    value: payment.invoice?.updatedAt
                      ? formatDate(payment.invoice.updatedAt, true)
                      : "—",
                    icon: Clock,
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    className={arClass(`flex items-center justify-between p-3 rounded-lg transition-all max-sm:p-2 ${
                      theme.darkMode
                        ? "hover:bg-slate-800/50"
                        : "hover:bg-slate-50"
                    }`)}
                  >
                    <div className={arClass("flex items-center gap-3 max-sm:gap-2")}>
                      <div
                        className={arClass(`p-2 rounded-lg max-sm:p-1.5 ${
                          theme.darkMode ? "bg-slate-700/50" : "bg-slate-100"
                        }`)}
                      >
                        <item.icon
                          className={arClass(`w-4 h-4 max-sm:w-3.5 max-sm:h-3.5 ${
                            theme.darkMode ? "text-blue-400" : "text-blue-600"
                          }`)}
                        />
                      </div>
                      <span
                        className={arClass(`text-sm font-medium max-sm:text-xs ${
                          theme.darkMode ? "text-slate-400" : "text-slate-600"
                        }`)}
                      >
                        {item.label}
                      </span>
                    </div>

                    {item.label === textTranslator("Payment Method", "PAYMENTS") && payment?.method ? (
                      <span className={arClass(getMethod(payment.method).style)}>
                        {getMethod(payment.method).icon}
                        <span className={arClass("max-sm:truncate max-sm:max-w-[80px]")}>
                          {getMethod(payment.method).label}
                        </span>
                      </span>
                    ) : (
                      <span
                        className={arClass(`font-semibold text-sm max-sm:text-xs ${
                          theme.darkMode ? "text-slate-200" : "text-slate-800"
                        }`)}
                      >
                        {safeDisplay(item.value, "—")}
                      </span>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Patient Summary & QR */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="group"
        >
          <Card
            className={arClass(`${
              theme.darkMode
                ? "bg-slate-800/50 border border-slate-700/50"
                : "bg-white border border-slate-200"
            } transition-all duration-300 overflow-hidden`)}
          >
            <div
              className={arClass(`relative px-6 py-4 border-b max-sm:px-4 max-sm:py-3 ${
                theme.darkMode
                  ? "border-slate-700/50 bg-purple-900/30"
                  : "border-slate-200 bg-purple-50"
              }`)}
            >
              <div className={arClass("flex items-center gap-4 max-sm:gap-3")}>
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className={arClass(`p-3 rounded-xl max-sm:p-2 ${
                    theme.darkMode ? "bg-purple-600" : "bg-purple-500"
                  } shadow-sm`)}
                >
                  <FaQrcode className={arClass("w-6 h-6 text-white max-sm:w-5 max-sm:h-5")} />
                </motion.div>
                <div className={arClass("flex justify-between items-center w-full max-sm:flex-col max-sm:items-start max-sm:gap-2")}>
                  <h3
                    className={arClass(`text-xl font-bold max-sm:text-lg ${
                      theme.darkMode ? "text-white" : "text-slate-900"
                    }`)}
                  >
                    <Text>Invoice QR Code</Text>
                  </h3>
                </div>
              </div>
            </div>

            <div className={arClass("p-6 max-sm:p-4")}>
              <div className={arClass("grid grid-cols-1 gap-6 max-sm:gap-4")}>
                {/* QR Code */}
                <div>
                  <h4
                    className={arClass(`font-semibold mb-4 flex items-center gap-2 text-sm max-sm:text-xs max-sm:mb-3 ${
                      theme.darkMode ? "text-white" : "text-slate-900"
                    }`)}
                  >
                    <FaQrcode
                      className={arClass(`w-5 h-5 max-sm:w-4 max-sm:h-4 ${
                        theme.darkMode ? "text-green-400" : "text-green-600"
                      }`)}
                    />
                    <Text>Payment QR Code</Text>
                  </h4>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className={`${
                      theme.darkMode
                        ? "bg-slate-800/50 border border-slate-700/50"
                        : "bg-slate-50 border border-slate-200"
                    } flex flex-col items-center p-4 rounded-xl max-sm:p-3`}
                  >
                    {qrSrc ? (
                      <img
                        src={qrSrc}
                        alt="Invoice QR Code"
                        className={`w-1/2 rounded-lg border-2 shadow-lg p-2 bg-white max-sm:w-2/3 ${
                          theme.darkMode
                            ? "border-green-500/20"
                            : "border-green-200"
                        }`}
                      />
                    ) : (
                      <div
                        className={`w-[120px] h-[120px] flex items-center justify-center rounded-lg border-2 border-dashed max-sm:w-[100px] max-sm:h-[100px] ${
                          theme.darkMode
                            ? "border-slate-600"
                            : "border-slate-300"
                        }`}
                      >
                        <div className="text-center">
                          <FaQrcode
                            className={`w-6 h-6 mx-auto mb-1 max-sm:w-5 max-sm:h-5 ${
                              theme.darkMode
                                ? "text-slate-600"
                                : "text-slate-400"
                            }`}
                          />
                          <p
                            className={arClass(`text-xs max-sm:text-xss ${
                              theme.darkMode
                                ? "text-slate-600"
                                : "text-slate-400"
                            }`)}
                          >
                            <Text>Generating QR...</Text>
                          </p>
                        </div>
                      </div>
                    )}
                    <p
                      className={arClass(`text-xs mt-2 text-center flex items-center gap-2 max-sm:text-xss max-sm:mt-1 ${
                        theme.darkMode ? "text-slate-400" : "text-slate-600"
                      }`)}
                    >
                      <span
                        className={`w-2 h-2 rounded-full animate-pulse max-sm:w-1.5 max-sm:h-1.5 ${
                          theme.darkMode ? "bg-green-400" : "bg-green-500"
                        }`}
                      />
                      <Text>Scan to view payment details</Text>
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Progress bar for loading actions */}
      {(downloading || printing || sharing) && (
        <div className={arClass("fixed left-1/2 -translate-x-1/2 bottom-6 z-50 w-64 max-sm:w-48")}>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-2 bg-blue-500 animate-[progress_1.5s_linear_infinite]" style={{ width: "40%" }} />
          </div>
        </div>
      )}
    </div>
  );
};

const GenerateInvoiceData = ({ payment }) => {
  const generateInvoiceKey = `generate_payment_${payment.id}`;
  const dispatch = useDispatch();
  const theme = useTheme();
  const handleInvoice = () => {
    dispatch(apiRequest("POST", generateInvoiceKey));
    api
      .post(`payments/generate-invoice/${payment.id}/`, payment)
      .then((res) => {
        dispatch(
          apiSuccess(
            "POST",
            generateInvoiceKey,
            textTranslator("invoiceCreatedSuccessfully", "PAYMENTS")
          )
        );
        dispatch(
          showAlert({
            type: "success",
            title: textTranslator("invoiceGenerated", "PAYMENTS"),
            message: textTranslator("invoiceCreatedSuccessfully", "PAYMENTS"),
          })
        );
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.error || error.message || textTranslator("failedToGenerateInvoice", "PAYMENTS");
        dispatch(apiFetchError(errorMessage, "POST", generateInvoiceKey));
        dispatch(
          showAlert({
            type: "error",
            title: textTranslator("generateFailed", "PAYMENTS"),
            message: errorMessage,
          })
        );
      });
  };

  return (
    <div className={arClass("space-y-6 max-sm:space-y-4")}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={arClass(`relative overflow-hidden rounded-2xl p-8 max-sm:p-4 max-sm:rounded-xl ${
          theme.darkMode
            ? "bg-slate-800 border border-slate-700"
            : "bg-white border border-slate-200"
        }`)}
      >
        <div className={arClass("relative z-10 flex items-start justify-between max-sm:flex-col max-sm:gap-4")}>
          <div className={arClass("flex items-center gap-4 max-sm:gap-3")}>
            <motion.div
              animate={{
                opacity: [1, 0.8, 1],
                y: [0, -4, 0],
                rotate: [0, 2, -2, 0],
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className={arClass(`p-4 rounded-2xl max-sm:p-3 max-sm:rounded-xl ${
                theme.darkMode ? "bg-blue-600" : "bg-blue-500"
              }`)}
            >
              <FaFileAlt className={arClass("w-8 h-8 text-white max-sm:w-6 max-sm:h-6")} />
            </motion.div>
            <div className={arClass("max-sm:flex-1 max-sm:min-w-0")}>
              <h2
                className={arClass(`text-3xl font-bold max-sm:text-lg max-sm:truncate ${
                  theme.darkMode ? "text-white" : "text-slate-900"
                }`)}
              >
                <Text>Invoice Details</Text>
              </h2>
              <p
                className={arClass(`text-sm mt-1 max-sm:text-xs max-sm:mt-0.5 ${
                  theme.darkMode ? "text-slate-400" : "text-slate-600"
                }`)}
              >
                <Text>Complete invoice information and billing summary</Text>
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <Card className={arClass("p-6 max-sm:p-4")}>
        <div className={arClass("flex flex-col items-center justify-center text-center py-8")}>
          <FaFileAlt className={arClass(`w-16 h-16 mb-4 ${
            theme.darkMode ? "text-slate-400" : "text-slate-300"
          }`)} />
          <h3 className={arClass(`text-lg font-bold mb-3 ${
            theme.darkMode ? "text-white" : "text-slate-900"
          }`)}>
            <Text>noInvoice</Text>
          </h3>
          <p className={arClass(`text-sm mb-6 max-w-md ${
            theme.darkMode ? "text-slate-400" : "text-slate-600"
          }`)}>
            <Text>noInvoiceHere</Text>
          </p>
          <button
            className={arClass(`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              theme.darkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`)}
            onClick={handleInvoice}
          >
            <FaFileAlt className="w-4 h-4" />
            <Text>generateInvoice</Text>
          </button>
        </div>
      </Card>
    </div>
  );
};

const Invoice = ({ payment,  loading }) => {
  const theme = useTheme();
  if (loading || !payment) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={arClass("space-y-6 max-sm:space-y-4")}
      >
        {/* Stats Grid Skeleton */}
        <div className={arClass("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-sm:grid-cols-2 max-sm:gap-3")}>
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>

        {/* Content Grid Skeleton */}
        <div className={arClass("grid grid-cols-1 lg:grid-cols-2 gap-6 max-sm:gap-4")}>
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </motion.div>
    );
  }

  return (
    <div className={arClass("space-y-6 max-sm:space-y-4")}>
      {payment.invoice ? (
        <InvoiceView payment={payment} theme={theme} />
      ) : (
        <GenerateInvoiceData payment={payment} />
      )}
    </div>
  );
};

export default Invoice;